import { GoalAccountStructure } from '../../graphql/generated';
import { GoalAccountInvestmentType } from '../../types';
import { IconName } from '../icons';
import { useCurrentUser } from '../user';

interface GoalAccountStructureInfo {
    value: GoalAccountStructure;
    title: string;
    description: (context: {
        activePartnershipId: string | null | undefined;
    }) => string;
    shortDescription: string;
    investmentTypes: GoalAccountInvestmentType[];
    iconName: IconName;
}

export const infoByGoalAccountStructure: Record<
    GoalAccountStructure,
    GoalAccountStructureInfo
> = {
    JOINT: {
        value: 'JOINT',
        title: 'Joint',
        description: () =>
            'You both have equal ownership over the investment and can make changes / adjustments at any time.',
        shortDescription: 'You both contribute and both own this money.',
        investmentTypes: ['HIGH_YIELD_CASH', 'DIRECT_INDEXING'],
        iconName: 'JointPlentyAccount',
    },
    SPLIT: {
        value: 'SPLIT',
        title: 'Split',
        description: () =>
            'You own whatever you’ve contributed, and can only make changes to your own portfolio. You’ll see side-by-side progress.',
        shortDescription:
            'You contribute individually and own your portion of the money.',
        investmentTypes: ['HIGH_YIELD_CASH', 'CLASSIC_PORTFOLIO'],
        iconName: 'SplitPlentyAccount',
    },
    PRIVATE: {
        value: 'PRIVATE',
        title: 'Private',
        description: ({ activePartnershipId }) =>
            activePartnershipId
                ? "You own this investment and your partner will not be able to view it. You can see your goal in the What's mine mode."
                : 'You own this investment fully. Even if you invite a partner down the road, they will not be able to see it until you update permissions.',
        shortDescription: "It's your money, and only you contribute.",
        investmentTypes: ['HIGH_YIELD_CASH', 'CLASSIC_PORTFOLIO'],
        iconName: 'PrivatePlentyAccount',
    },
};

export const ACCOUNT_STRUCTURE_OPTIONS: GoalAccountStructureInfo[] = [
    infoByGoalAccountStructure.JOINT,
    infoByGoalAccountStructure.SPLIT,
    infoByGoalAccountStructure.PRIVATE,
];

export const useAccountStructureChoices = () => {
    const { plentyJointAccountStatus, activePartnershipId } = useCurrentUser();

    const canChooseSplitAccount = !!activePartnershipId;
    const canChooseJointAccount =
        plentyJointAccountStatus &&
        (plentyJointAccountStatus === 'ELIGIBLE' ||
            plentyJointAccountStatus === 'OPENED' ||
            plentyJointAccountStatus === 'OPENING' ||
            plentyJointAccountStatus === 'PARTNER_CONSENTED' ||
            plentyJointAccountStatus === 'USER_CONSENTED');

    return { canChooseJointAccount, canChooseSplitAccount };
};
